import React from 'react';
import { LightbulbIcon } from '@bizfirst/common-react';
import {
  ExecutionObserverConfig,
  ExecutionObserverConfigFactory,
  DEFAULT_ENTITY_TYPE_FILTER,
} from '../../models/ExecutionObserverConfig';
import '../../styles/tabs/BasicConfigTab.css';

interface BasicConfigTabProps {
  config: ExecutionObserverConfig;
  onChange: (config: ExecutionObserverConfig) => void;
  readonly?: boolean;
}

/**
 * Basic Configuration Tab
 *
 * Controls:
 * - Enable/disable observer
 * - Detail level (Compact, Detailed, Verbose)
 * - Preset buttons (Basic, Debug, Comprehensive)
 * - Quick enable/disable for process, thread, node
 */
export const BasicConfigTab: React.FC<BasicConfigTabProps> = ({
  config,
  onChange,
  readonly = false
}) => {
  const handleDetailLevelChange = (level: 'compact' | 'detailed' | 'verbose') => {
    onChange({
      ...config,
      detailLevel: level
    });
  };

  const handleQuickPreset = (preset: ExecutionObserverConfig) => {
    onChange(preset);
  };

  return (
    <fieldset className="basic-config-tab" disabled={readonly}>
      <legend>Basic Configuration</legend>

      {/* Enable Toggle */}
      <div className="form-group form-group--enable">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) =>
              onChange({
                ...config,
                enabled: e.target.checked
              })
            }
            disabled={readonly}
          />
          <span className="checkbox-text">Enable Observer</span>
        </label>
        <small className="help-text help-text--indented">
          When disabled, no events are observed or broadcast to clients
        </small>
      </div>

      {/* Detail Level */}
      <div className="form-group form-group--row">
        <label htmlFor="detail-level" className="field-label">Detail Level</label>
        <div className="field-control">
          <select
            id="detail-level"
            value={config.detailLevel || 'detailed'}
            onChange={(e) =>
              handleDetailLevelChange(e.target.value as 'compact' | 'detailed' | 'verbose')
            }
            disabled={readonly || !config.enabled}
          >
            <option value="compact">Compact — Event names only</option>
            <option value="detailed">Detailed — Event names + metadata</option>
            <option value="verbose">Verbose — Full event context + debug info</option>
          </select>
          <small className="help-text">
            Higher detail levels produce more data and use more bandwidth
          </small>
        </div>
      </div>

      {/* Quick Presets */}
      <div className="form-group form-group--row">
        <label className="field-label">Quick Presets</label>
        <div className="field-control">
          <div className="preset-buttons">
            <button
              type="button"
              className="preset-btn basic"
              onClick={() =>
                handleQuickPreset(ExecutionObserverConfigFactory.createBasic())
              }
              disabled={readonly}
              title="Minimal config: Process, Thread, and Node events only"
            >
              Basic
            </button>
            <button
              type="button"
              className="preset-btn debug"
              onClick={() =>
                handleQuickPreset(ExecutionObserverConfigFactory.createDebug())
              }
              disabled={readonly}
              title="Debug config: All event types and detailed metadata"
            >
              Debug
            </button>
            <button
              type="button"
              className="preset-btn comprehensive"
              onClick={() =>
                handleQuickPreset(ExecutionObserverConfigFactory.createComprehensive())
              }
              disabled={readonly}
              title="Comprehensive config: All possible filters enabled"
            >
              Comprehensive
            </button>
          </div>
          <small className="help-text">
            Presets override current configuration
          </small>
        </div>
      </div>

      {/* Quick Filters */}
      <div className="form-group form-group--row">
        <label className="field-label">Quick Filters</label>
        <div className="field-control">
          <div className="quick-filters">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={config.filter?.entityTypes?.includeProcess ?? true}
                onChange={(e) =>
                  onChange({
                    ...config,
                    filter: {
                      ...config.filter,
                      entityTypes: {
                        ...(config.filter?.entityTypes ?? DEFAULT_ENTITY_TYPE_FILTER),
                        includeProcess: e.target.checked
                      }
                    }
                  })
                }
                disabled={readonly || !config.enabled}
              />
              <span>Process Events</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={config.filter?.entityTypes?.includeThread ?? true}
                onChange={(e) =>
                  onChange({
                    ...config,
                    filter: {
                      ...config.filter,
                      entityTypes: {
                        ...(config.filter?.entityTypes ?? DEFAULT_ENTITY_TYPE_FILTER),
                        includeThread: e.target.checked
                      }
                    }
                  })
                }
                disabled={readonly || !config.enabled}
              />
              <span>Thread Events</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={config.filter?.entityTypes?.includeNode ?? true}
                onChange={(e) =>
                  onChange({
                    ...config,
                    filter: {
                      ...config.filter,
                      entityTypes: {
                        ...(config.filter?.entityTypes ?? DEFAULT_ENTITY_TYPE_FILTER),
                        includeNode: e.target.checked
                      }
                    }
                  })
                }
                disabled={readonly || !config.enabled}
              />
              <span>Node Events</span>
            </label>
          </div>
          <small className="help-text">
            Toggle main event categories quickly
          </small>
        </div>
      </div>

      {/* Info Box */}
      <div className="info-box">
        <p>
          <LightbulbIcon size={14} /> <strong>Tip:</strong> Use presets as a starting point, then customize
          in other tabs (Entity Filters, Event Types, Performance).
        </p>
      </div>
    </fieldset>
  );
};

export default BasicConfigTab;
